import articleApi from "../../services/article";
import aiTexterApi from "@services/aiTexter";
import commonApi from "../../services/common";
import alternativeArticleNoTypesApi from "../../services/alternativeArticleNoTypes";
import referenceTypeApi from "../../services/referenceTypes";
import keywordTypeApi from "../../services/keywordTypes";
import translationsApi from "../../services/articleTranslations";
import eventLogger from "../../services/ApiLogger/EventLogger";
import economicOperatorApi from "@services/economicOperator.ts";
import gpsrApi from "@services/gpsr.ts";
import _ from "lodash";
import i18n from "@/i18n";
import { MinWait } from "@util/timing";
import axios from "axios";

function defaultState() {
  return {
    masterdata: {},
    alternativeArticleNumbers: {},
    articleNotFound: false,
    articleNotInBaseCatalog: false,
    globalAttributes: {},
    variantAttributes: {},
    invariantAttributes: {},
    multiArticlevariantAttributes: {},
    erpSections: {},
    mediaAssets: {},
    prices: {},
    priceAttributes: {},
    keywords: {},
    classifications: {},
    references: {},
    referenceTypes: {},
    keywordTypes: {},
    catalogs: {},
    gpsr: {},
    translatedMasterdata: {},
    translatedGlobalAttributes: {},
    translatedVariantAttributes: {},
    translatedInvariantAttributes: {},
    translatedPriceAttributes: {},
    translatedMediaAssets: {},
    translatedKeywords: {},
    translatedGlobalAttributeSuggestions: {},
    translatedVariantAttributeSuggestions: {},
    translatedinVariantAttributeSuggestions: {},
    translatedPriceAttributeSuggestions: {},
    translatedMediaAssetsSuggestions: {},
    translatedKeywordSuggestions: {},
    globalAttributeValidationStatus: {},
    common: {
      articlestates: [],
      alternativeArticleNoTypes: [],
      users: [],
      loaded: false
    },
    metadata: {
      articleId: "",
      error: "",
      loaded: false,
      loading: false,
      variantAttributesLoading: false,
      update: {
        jobs: [],
        error: "",
        apiError: false,
        success: true,
        seen: true
      }
    },
    modified: {
      masterdata: false,
      attributeGroupPaths: false,
      alternativeArticleNumbers: false,
      globalAttributes: false,
      variantAttributes: false,
      invariantAttributes: false,
      multiArticlevariantAttributes: false,
      erpSections: false,
      mediaAssets: false,
      prices: false,
      priceAttributes: false,
      keywords: false,
      classifications: false,
      references: false,
      referenceTypes: false,
      keywordTypes: false,
      gpsr: false,
      translatedMasterdata: false,
      translatedGlobalAttributes: false,
      translatedVariantAttributes: false,
      translatedInvariantAttributes: false,
      translatedPriceAttributes: false,
      translatedMediaAssets: false,
      translatedKeywords: false
    },
    updateTasks: {
      masterdata: false,
      attributeGroupPaths: false,
      alternativeArticleNumbers: false,
      globalAttributes: false,
      variantAttributes: false,
      invariantAttributes: false,
      multiArticlevariantAttributes: false,
      erpSections: false,
      mediaAssets: false,
      prices: false,
      priceAttributes: false,
      keywords: false,
      classifications: false,
      references: false,
      referenceTypes: false,
      keywordTypes: false,
      gpsr: false,
      translatedMasterdata: false,
      translatedGlobalAttributes: false,
      translatedVariantAttributes: false,
      translatedInvariantAttributes: false,
      translatedPriceAttributes: false,
      translatedMediaAssets: false,
      translatedKeywords: false
    },
    updateErrors: {
      masterdata: undefined,
      attributeGroupPaths: undefined,
      alternativeArticleNumbers: undefined,
      globalAttributes: undefined,
      variantAttributes: undefined,
      invariantAttributes: undefined,
      multiArticlevariantAttributes: undefined,
      erpSections: undefined,
      mediaAssets: undefined,
      prices: undefined,
      priceAttributes: undefined,
      keywords: undefined,
      classifications: undefined,
      references: undefined,
      referenceTypes: undefined,
      keywordTypes: undefined,
      gpsr: undefined,
      translatedMasterdata: undefined,
      translatedGlobalAttributes: undefined,
      translatedVariantAttributes: undefined,
      translatedInvariantAttributes: undefined,
      translatedPriceAttributes: undefined,
      translatedMediaAssets: undefined,
      translatedKeywords: undefined
    },
    aiTexterStyles: {}
  };
}

export default {
  namespaced: true,
  state: {
    ...defaultState()
  },
  getters: {
    isSaving(state) {
      return Object.keys(state.updateTasks).some((x) => state.updateTasks[x]);
    },
    hasUnsavedData(state) {
      const sections = Object.keys(state.modified);
      return sections.some((x) => state.modified[x]);
    },
    lastRunSuccess(state) {
      return Object.keys(state.updateErrors).every((x) => !state.updateErrors[x]);
    },
    languagesForEditorServices(state, getters, rootState) {
      const enabledLanguages = rootState.metadata.contentLanguages.enabled;
      let languages = enabledLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));
      return languages;
    }
  },
  mutations: {
    "update-erp-attributes"(state, arg) {
      state.erpSections = arg;
    },
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    "refresh-article-masterdata"(state, arg) {
      const data = Object.assign({}, state.masterdata);
      data[arg.language] = arg.data;
      state.masterdata = data;
    },
    "refresh-article-alternative-articlenumbers"(state, payload) {
      state.alternativeArticleNumbers = payload.data;
    },
    "common-data-update-articlestate"(state, payload) {
      state.common.articlestates = payload;
    },
    "refresh-alternative-article-no-types"(state, args) {
      state.common.alternativeArticleNoTypes = args;
    },
    "refresh-reference-types"(state, args) {
      const data = Object.assign({}, state.referenceTypes);
      data[args.language] = args.data;
      state.referenceTypes = data;
    },
    "refresh-keyword-types"(state, args) {
      state.keywordTypes = args;
    },
    "refresh-article-catalogs"(state, args) {
      let data = Object.assign({}, state.catalogs);
      data = args.data;
      state.catalogs = data;
    },
    "common-data-loaded"(state, payload) {
      state.common.loaded = payload;
    },
    "article-error"(state, payload) {
      state.metadata.error = payload;
    },
    "article-loaded"(state, payload) {
      state.metadata.loaded = payload;
    },
    "change-article"(state, payload) {
      state.metadata.articleId = payload;
    },
    "article-loading-state-update"(state, loading) {
      state.metadata.loading = loading;
    },
    "variant-attributes-loading-state-update"(state, loading) {
      state.metadata.variantAttributesLoading = loading;
    },
    "refresh-article-global-attributes"(state, args) {
      const data = Object.assign({}, state.globalAttributes);
      data[args.language] = args.data;
      state.globalAttributes = data;
    },
    "refresh-article-variant-attributes"(state, args) {
      const data = Object.assign({}, state.variantAttributes);
      data[args.language] = args.data;
      state.variantAttributes = data;
    },
    "refresh-article-invariant-attributes"(state, args) {
      const data = Object.assign({}, state.invariantAttributes);
      data[args.language] = args.data;
      state.invariantAttributes = data;
    },
    "refresh-multi-article-variant-attributes"(state, arg) {
      state.multiArticlevariantAttributes = arg.data;
    },
    "refresh-article-erp-sections"(state, arg) {
      const data = Object.assign({}, state.erpSections);
      data[arg.language] = arg.data;
      state.erpSections = data;
    },
    "refresh-article-prices"(state, arg) {
      state.prices = arg.data;
    },
    "refresh-article-price-attributes"(state, arg) {
      const data = Object.assign({}, state.priceAttributes);
      data[arg.language] = arg.data;
      state.priceAttributes = data;
    },
    "refresh-article-references"(state, arg) {
      const data = Object.assign({}, state.references);
      data[arg.language] = arg.data;
      state.references = data;
    },
    "refresh-article-keywords"(state, arg) {
      const data = Object.assign({}, state.keywords);
      data[arg.language] = arg.data;
      state.keywords = data;
    },
    "refresh-article-classifications"(state, arg) {
      const data = Object.assign({}, state.classifications);
      data[arg.language] = arg.data;
      state.classifications = data;
    },
    "refresh-article-media-assets"(state, arg) {
      const data = Object.assign({}, state.mediaAssets);
      data[arg.language] = arg.data;
      state.mediaAssets = data;
    },
    "refresh-gpsr"(state, args) {
      state.gpsr = args;
    },
    "set-gpsr-assignments"(state, args) {
      state.gpsr.manufacturerId = args.manufacturerId;
      state.gpsr.responsibleInEuId = args.responsibleInEuId;
    },
    "set-article-not-found"(state, arg) {
      state.articleNotFound = arg;
    },
    "set-article-not-in-base-catalog"(state, arg) {
      state.articleNotInBaseCatalog = arg;
    },
    "translation-article-master-data"(state, arg) {
      state.translatedMasterdata = arg.data;
    },
    "translation-article-global-attributes"(state, arg) {
      state.translatedGlobalAttributes = arg.data;
    },
    "translation-article-variant-attributes"(state, arg) {
      state.translatedVariantAttributes = arg.data;
    },
    "translation-article-invariant-attributes"(state, arg) {
      state.translatedInvariantAttributes = arg.data;
    },
    "translation-article-price-attributes"(state, arg) {
      state.translatedPriceAttributes = arg.data;
    },
    "translation-article-media-assets"(state, arg) {
      state.translatedMediaAssets = arg.data;
    },
    "translation-article-keywords"(state, arg) {
      state.translatedKeywords = arg.data;
    },
    "set-global-attribute-validation-status"(state, arg) {
      state.globalAttributeValidationStatus = arg.data;
    },
    "set-variant-attribute-validation-status"(state, arg) {
      state.variantAttributeValidationStatus = arg.data;
    },
    "set-multi-article-variant-attribute-validation-status"(state, arg) {
      state.multiArticleVariantAttributeValidationStatus = arg.data;
    },
    "set-invariant-attribute-validation-status"(state, arg) {
      state.invariantAttributeValidationStatus = arg.data;
    },
    "set-erp-attribute-validation-status"(state, arg) {
      state.erpAttributeValidationStatus = arg.data;
    },
    "update-save-status"(state, arg) {
      const sectionState = Object.assign({}, state.updateTasks);
      sectionState[arg.section] = arg.running;
      state.updateTasks = sectionState;
      state.metadata.update.seen = arg.seen == undefined ? false : arg.seen;
      if (arg.error) {
        state.updateErrors[arg.section] = arg.apiError ? { apiError: true, response: arg.error.response } : { apiError: false, exception: arg.error };
        state.metadata.update.apiError = arg.apiError;
      }
      if (arg.success != undefined) state.metadata.update.success = arg.success;
    },
    "reset-update-status"(state) {
      state.updateTasks = {
        masterdata: false,
        attributeGroupPaths: false,
        alternativeArticleNumbers: false,
        globalAttributes: false,
        variantAttributes: false,
        invariantAttributes: false,
        multiArticlevariantAttributes: false,
        erpSections: false,
        mediaAssets: false,
        prices: false,
        priceAttributes: false,
        keywords: false,
        classifications: false,
        references: false,
        referenceTypes: false,
        keywordTypes: false,
        translatedMasterdata: false,
        translatedGlobalAttributes: false,
        translatedVariantAttributes: false,
        translatedInvariantAttributes: false,
        translatedPriceAttributes: false,
        translatedMediaAssets: false,
        translatedKeywords: false
      };
      state.updateErrors = {
        masterdata: undefined,
        attributeGroupPaths: undefined,
        alternativeArticleNumbers: undefined,
        globalAttributes: undefined,
        variantAttributes: undefined,
        invariantAttributes: undefined,
        multiArticlevariantAttributes: undefined,
        erpSections: undefined,
        mediaAssets: undefined,
        prices: undefined,
        priceAttributes: undefined,
        keywords: undefined,
        classifications: undefined,
        references: undefined,
        referenceTypes: undefined,
        keywordTypes: undefined,
        translatedMasterdata: undefined,
        translatedGlobalAttributes: undefined,
        translatedVariantAttributes: undefined,
        translatedInvariantAttributes: undefined,
        translatedPriceAttributes: undefined,
        translatedMediaAssets: undefined,
        translatedKeywords: undefined
      };
    },
    "notification-seen"(state) {
      state.metadata.update.seen = true;
    },
    "mark-dirty-status"(state, args) {
      state.modified[args.section] = args.dirty;
    },
    "clear-dirty-status"(state) {
      state.modified = {
        masterdata: false,
        attributeGroupPaths: false,
        alternativeArticleNumbers: false,
        globalAttributes: false,
        variantAttributes: false,
        invariantAttributes: false,
        multiArticlevariantAttributes: false,
        erpSections: false,
        mediaAssets: false,
        prices: false,
        priceAttributes: false,
        keywords: false,
        classifications: false,
        references: false,
        referenceTypes: false,
        keywordTypes: false,
        translatedMasterdata: false,
        translatedGlobalAttributes: false,
        translatedVariantAttributes: false,
        translatedInvariantAttributes: false,
        translatedPriceAttributes: false,
        translatedMediaAssets: false,
        translatedKeywords: false
      };
    },
    "refresh-user-list"(state, payload) {
      state.common.users = payload;
    }
  },
  actions: {
    updateErpAttributes({ commit }, args) {
      commit("update-erp-attributes", args);
    },
    loadAvailableAiTexterLanguages: async function (context) {
      const enabledLanguages = context.rootState.metadata.contentLanguages.enabled;
      const result = {};
      for (const lang of enabledLanguages) {
        try {
          result[lang.languageCode] = await aiTexterApi.getAiTextGenerationStyles(lang.languageCode);
        } catch (error) {
          result[lang.languageCode] = [];
          continue;
        }
      }
      context.state.aiTexterStyles = result;
    },
    loadMasterDataIfNecessary: async function ({ state, dispatch, rootState }, args) {
      const requestedLanguages = rootState.metadata.contentLanguages.enabled;
      if (
        requestedLanguages.length !== Object.keys(state.masterdata).length ||
        state.masterdata[requestedLanguages[0].languageCode].articleId != args.articleId
      ) {
        return await dispatch("loadMasterData", args);
      }
    },
    loadMasterData: async function (context, args) {
      const data = await articleApi.getMasterData(args);

      context.commit("refresh-article-masterdata", {
        language: args.language,
        data: data
      });

      return data;
    },
    loadAlternativeArticleNumbers: async function (context, args) {
      const data = await articleApi.getAlternativeArticleNumbers(args);
      context.commit("refresh-article-alternative-articlenumbers", {
        data: data
      });
    },
    loadGlobalAttributes: async function (context, args) {
      const data = await articleApi.getGlobalAttributes(args);
      context.commit("refresh-article-global-attributes", {
        language: args.language,
        data: data
      });
    },
    loadVariantAttributes: async function (context, args) {
      const data = await articleApi.getVariantAttributes(args);
      context.commit("refresh-article-variant-attributes", {
        language: args.language,
        data: data
      });
    },
    loadVariantAttributesWithlocalizations: async function (context, args) {
      context.commit("variant-attributes-loading-state-update", true);
      let languages = args.languages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = new Set(languages);
      languages = Array.from(languages);
      const tasks = [];

      languages.forEach((lang) => {
        tasks.push(context.dispatch("loadVariantAttributes", { articleId: args.articleId, language: lang }));
      });
      try {
        await Promise.all(tasks);
      } finally {
        context.commit("variant-attributes-loading-state-update", true);
      }
    },
    loadInvariantAttributes: async function (context, args) {
      const data = await articleApi.getInvariantAttributes(args);
      context.commit("refresh-article-invariant-attributes", {
        language: args.language,
        data: data
      });
    },
    loadMultiArticleVariantAttributes: async function (context, args) {
      const requestedLanguages = context.rootState.metadata.contentLanguages.enabled;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = new Set(languages);
      languages = Array.from(languages);
      const attributes = {};

      const tasks = languages.map(async (lang) => {
        const data = await articleApi.getMultiArticleVariantAttributes(
          args.articleId || context.state.metadata.articleId,
          lang,
          args.articlePagesize,
          args.articlePageOffset,
          args.attributePagesize,
          args.attributePageOffset
        );
        attributes[lang] = data;
      });

      await Promise.all(tasks);
      context.commit("refresh-multi-article-variant-attributes", {
        data: attributes
      });
    },
    loadErpAttributes: async function (context, args) {
      const data = await articleApi.getErpSections(args);
      context.commit("refresh-article-erp-sections", {
        language: args.language,
        data: data
      });
    },
    loadPrices: async function (context, args) {
      const data = await articleApi.getPrices(args);
      context.commit("refresh-article-prices", {
        data: data
      });
    },
    loadPriceAttributes: async function (context, args) {
      const data = await articleApi.getPriceAttributes(args);
      context.commit("refresh-article-price-attributes", {
        language: args.language,
        data: data
      });
    },
    loadKeywords: async function (context, args) {
      const data = await articleApi.getKeywords(args);
      context.commit("refresh-article-keywords", {
        language: args.language,
        data: data
      });
    },
    loadClassifications: async function (context, args) {
      const data = await articleApi.getClassifications(args);
      context.commit("refresh-article-classifications", {
        language: args.language,
        data: data
      });
    },
    loadReferences: async function (context, args) {
      const data = await articleApi.getReferences(args);
      context.commit("refresh-article-references", {
        language: args.language,
        data: data
      });
    },
    loadMediaAssets: async function (context, args) {
      const data = await articleApi.getMediaAssets(args);
      context.commit("refresh-article-media-assets", {
        language: args.language,
        data: data
      });
    },
    loadCatalogs: async function (context, args) {
      const data = await articleApi.getCatalogs(args);
      context.commit("refresh-article-catalogs", {
        language: args.language,
        data: data
      });
    },
    loadReferenceTypes: async function (context, args) {
      try {
        const referenceTypes = await referenceTypeApi.get(args);
        context.commit("refresh-reference-types", {
          language: args.languageCode,
          data: referenceTypes
        });
      } catch (error) {
        console.error("error on loading referencetypes", error);
      }
    },
    loadKeywordTypes: async function ({ commit, getters }) {
      try {
        const languages = getters.languagesForEditorServices;
        const keywordTypes = await keywordTypeApi.get(languages);
        commit("refresh-keyword-types", keywordTypes);
      } catch (error) {
        console.error("error on loading keywordTypes", error);
      }
    },
    loadAllStates: async function ({ commit }) {
      try {
        const states = await articleApi.getAllArticleStates();
        commit("common-data-update-articlestate", states);
      } catch (error) {
        commit("common-data-loaded", false);
        console.error("List of article states failed loading", error);
      }
    },
    loadAllUsers: async function ({ commit }) {
      try {
        const users = await commonApi.getAllUsers();
        commit("refresh-user-list", users);
      } catch (error) {
        console.error("List of users failed loading", error);
      }
    },
    loadAllCommonData: async function ({ state, commit, dispatch }, args) {
      if (state.common.loaded && !args.force) return Promise.resolve();

      commit("common-data-loaded", false);
      const states = dispatch("loadAllStates");
      const users = dispatch("loadAllUsers");
      const numberTypes = dispatch("loadAlternativeArticleNoTypes");
      const referenceTypes = dispatch("loadReferenceTypes", { languageCode: args.language });
      const keywordTypes = dispatch("loadKeywordTypes");

      try {
        await Promise.all([states, users, numberTypes, referenceTypes, keywordTypes]);
        commit("common-data-loaded", true);
      } catch (error) {
        console.error("loadAllCommonData failed", error);
      }
    },
    loadAlternativeArticleNoTypes: async function ({ commit }) {
      try {
        const alternativeArticleNoTypes = await alternativeArticleNoTypesApi.get();
        commit("refresh-alternative-article-no-types", alternativeArticleNoTypes);
      } catch (error) {
        console.error("error on loading AlternativeArticleNoTypes", error);
      }
    },
    loadArticleWithLocalizations: async function ({ dispatch, commit, rootState }, args) {
      const logContext = eventLogger.beginContext("loadArticleWithLocalizations");
      logContext.info("Initiating article load", { ...args }).alsoToConsole();
      try {
        const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
        let languages = requestedLanguages
          .map((x) => [x.languageCode, x.fallbackLanguageCode])
          .flat()
          .filter((x) => !!x);
        languages = Array.from(new Set(languages));
        const tasks = [];

        commit("article-loading-state-update", true);
        commit("change-article", args.articleId);
        commit("article-loaded", false);

        tasks.push(dispatch("loadAllCommonData", { language: languages[0] }));

        logContext.info("Article loading completed successfully", { ...args }).alsoToConsole();
        commit("article-loaded", true);
      } catch (error) {
        logContext.error("Article loading completed with errors", { ...args, exception: error }).alsoToConsole();
        console.error("loading completed with errors for article", args, error);
        commit("article-loaded", false);
        if (error.response.data.error == "ArticleNotExist") {
          commit("set-article-not-found", true);
        }
        if (error.response.data.error == "ArticleNotInBaseCatalog") {
          commit("set-article-not-in-base-catalog", true);
        }
      } finally {
        logContext.complete();
        commit("article-loading-state-update", false);
      }
    },
    loadArticleAttributesWithLocalizations: async function ({ dispatch, commit }, args) {
      const requestedLanguages = args.languages;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = new Set(languages);
      const tasks = [];

      commit("article-loading-state-update", true);
      languages.forEach((lang) => {
        tasks.push(dispatch("loadVariantAttributes", { articleId: args.articleId, language: lang }));
        tasks.push(dispatch("loadInvariantAttributes", { articleId: args.articleId, language: lang }));
      });
      try {
        await Promise.all(tasks);
      } catch (error) {
        console.error("loading-complete with errors for article attributes", args, error);
      }

      commit("article-loading-state-update", false);
    },
    loadTranslatedArticlesWithLocalization: async function ({ dispatch, rootState }, args) {
      const logContext = eventLogger.beginContext("loadTranslatedArticlesWithLocalization");
      const visibleSection = {
        masterdata: rootState.userSettings.ShowTranslatedMasterdata === "true" || rootState.userSettings.ShowTranslatedMasterdata === true,
        globalAttributes: rootState.userSettings.ShowTranslatedGlobalAttributes === "true" || rootState.userSettings.ShowTranslatedGlobalAttributes === true,
        invariantAttributes:
          rootState.userSettings.ShowTranslatedInvariantAttributes === "true" || rootState.userSettings.ShowTranslatedInvariantAttributes === true,
        variantAttributes: rootState.userSettings.ShowTranslatedVariantAttributes === "true" || rootState.userSettings.ShowTranslatedVariantAttributes === true,
        keywords: rootState.userSettings.ShowTranslatedKeywords === "true" || rootState.userSettings.ShowTranslatedKeywords === true,
        mediaAssets: rootState.userSettings.ShowTranslatedMediaAssets === "true" || rootState.userSettings.ShowTranslatedMediaAssets === true,
        priceAttributes: rootState.userSettings.ShowTranslatedPriceAttributes === "true" || rootState.userSettings.ShowTranslatedPriceAttributes === true
      };
      const { languages: requestedLanguages, ...rest } = args;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));
      const tasks = [];

      logContext.info("Loading translation tab for article editor", rest);
      if (visibleSection.masterdata) {
        tasks.push(
          dispatch("loadTranslatedArticleMasterdata", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.globalAttributes) {
        tasks.push(
          dispatch("loadTranslatedArticleGlobalAttribute", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.variantAttributes) {
        tasks.push(
          dispatch("loadTranslatedArticleVariantAttribute", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.invariantAttributes) {
        tasks.push(
          dispatch("loadTranslatedArticleInvariantAttribute", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.priceAttributes) {
        tasks.push(
          dispatch("loadTranslatedArticlePriceAttribute", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.mediaAssets) {
        tasks.push(
          dispatch("loadTranslatedArticleMediaAsset", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      if (visibleSection.keywords) {
        tasks.push(
          dispatch("loadTranslatedArticleKeywords", {
            variationGroupId: args.variationGroupId,
            languages,
            pagesize: args.pagesize,
            pageOffset: args.pageOffset,
            logContext
          })
        );
      }
      try {
        await Promise.all(tasks);
        logContext.info("Loading translation tab for article editor completed successfully", rest);
      } catch (error) {
        console.error("loading-complete with errors for article translation", args, error);
        logContext.info("Loading translation tab for article editor completed with errors", { ...rest, exception: error });
      }
      logContext.complete();
    },
    loadTranslatedArticleMasterdata: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation masterdata for article editor translation tab", rest);
      const tasks = languages.map(async (languageCode) => {
        const data = await translationsApi.getMasterData(args.variationGroupId, languageCode, args.pagesize, args.pageOffset);
        model[languageCode] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation masterdata for article editor translation tab completed", rest);
        context.commit("translation-article-master-data", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation masterdata", { ...rest, exception: error });
        console.error("load translated article master data failed with error", error);
      }
    },
    loadTranslatedArticleGlobalAttribute: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation global attributes for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getGlobalAttributes(args.variationGroupId, lang, args.pagesize, args.pageOffset);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation global attributes for article editor translation tab completed", rest);
        context.commit("translation-article-global-attributes", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation global attributes failed", { ...rest, exception: error });
        console.error("load translated article global attributes failed with error", error);
      }
    },
    loadTranslatedArticleVariantAttribute: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation variant attributes for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getVariantAttributes(args.variationGroupId, lang, args.pagesize, args.pageOffset);
        model[lang] = data;
      });
      try {
        await Promise.all(tasks);
        logContext.info("Loading translation variant attributes for article editor translation tab completed", rest);
        context.commit("translation-article-variant-attributes", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation variant attributes failed", { ...rest, exception: error });
        console.error("load translated article variant attributes failed with error", error);
      }
    },
    loadTranslatedArticleInvariantAttribute: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation invariant attributes for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getInvariantAttributes(args.variationGroupId, lang, args.pagesize, args.pageOffset);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation invariant attributes for article editor translation tab completed", rest);
        context.commit("translation-article-invariant-attributes", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation global attributes failed", { ...rest, exception: error });
        console.error("load translated article invariant attributes failed with error", error);
      }
    },
    loadTranslatedArticlePriceAttribute: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation price attributes for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getPriceAttributes(args.variationGroupId, lang, args.pagesize, args.pageOffset);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation price attributes for article editor translation tab completed", rest);
        context.commit("translation-article-price-attributes", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation price attributes failed", { ...rest, exception: error });
        console.error("load translated article price attributes failed with error", error);
      }
    },
    loadTranslatedArticleMediaAsset: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation media assets for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getMediaAssets(args.variationGroupId, lang, args.pagesize, args.pageOffset);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation media assets for article editor translation tab completed", rest);
        context.commit("translation-article-media-assets", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation media assets failed", { ...rest, exception: error });
        console.error("load translated article media assets failed with error", error);
      }
    },
    loadTranslatedArticleKeywords: async function (context, args) {
      const model = {};
      const { logContext, languages, ...rest } = args;
      logContext.info("Loading translation keywords for article editor translation tab", rest);
      const tasks = languages.map(async (lang) => {
        const data = await translationsApi.getKeywords(args.variationGroupId, lang, context.state.metadata.articleId, args.pagesize, args.pageOffset);
        model[lang] = data;
      });

      try {
        await Promise.all(tasks);
        logContext.info("Loading translation keywords for article editor translation tab completed", rest);
        context.commit("translation-article-keywords", {
          data: model
        });
      } catch (error) {
        logContext.error("Loading translation article keywords failed", { ...rest, exception: error });
        console.error("load translated article keywords failed with error", error);
      }
    },
    loadGpsr: async function (context) {
      const logger = eventLogger.beginContext("loadGPSRDataOfArticle");
      try {
        const languageCodes = _.chain(context.rootState.metadata.contentLanguages.enabled)
          .flatMap((lang) => [lang.languageCode, lang.fallbackLanguageCode])
          .filter((languageCode) => languageCode)
          .uniq()
          .value();
        if (context.state.metadata.articleId && context.state.metadata.articleId !== "") {
          logger.info(`Loading GPSR data for article ${context.state.metadata.articleId}`).alsoToConsole();
          const economicOperators = await economicOperatorApi.getEconomicOperators();
          const economicOperatorsAssignedToArticle = await gpsrApi.getEconomicOperatorsAssignedToArticle(context.state.metadata.articleId);
          const economicOperatorAttributes = await economicOperatorApi.getEconomicOperatorAttributes();
          const linkData = await gpsrApi.getResolvedArticleLinkData(context.state.metadata.articleId, languageCodes);

          context.commit("refresh-gpsr", {
            economicOperators: economicOperators,
            manufacturerId: economicOperatorsAssignedToArticle.manufacturerId,
            responsibleInEuId: economicOperatorsAssignedToArticle.responsibleInEuId,
            economicOperatorAttributes: economicOperatorAttributes,
            linkData: linkData
          });
        } else {
          context.commit("refresh-gpsr", {
            economicOperators: [],
            manufacturerId: null,
            responsibleInEuId: null,
            economicOperatorAttributes: [],
            linkData: {}
          });
        }
      } catch (error) {
        logger.error("Loading GPSR data for article failed", { articleId: context.state.metadata.articleId, error: error }).alsoToConsole();
      } finally {
        logger.complete();
      }
    },
    loadGlobalAttributeValidationStatus: async function (context, args) {
      const data = await articleApi.getAttributeValidationStatus({
        ...args,
        attributeType: "globalAttribute"
      });
      context.commit("set-global-attribute-validation-status", { data });
    },
    loadVariantAttributeValidationStatus: async function (context, args) {
      const data = await articleApi.getAttributeValidationStatus({
        ...args,
        attributeType: "tableAttribute"
      });
      context.commit("set-variant-attribute-validation-status", { data });
    },
    loadMultiArticleVariantAttributeValidationStatus: async function (context, args) {
      const data = await articleApi.getMultiArticleVariantAttributesValidationStatus(args);
      context.commit("set-multi-article-variant-attribute-validation-status", { data });
    },
    loadInvariantAttributeValidationStatus: async function (context, args) {
      const data = await articleApi.getAttributeValidationStatus({
        ...args,
        attributeType: "textAttribute"
      });
      context.commit("set-invariant-attribute-validation-status", { data });
    },
    loadErpAttributeValidationStatus: async function (context, args) {
      const data = await articleApi.getAttributeValidationStatus({
        ...args,
        attributeType: "erpAttribute"
      });
      context.commit("set-erp-attribute-validation-status", { data });
    },
    saveMasterData: async function (context, args) {
      const logger = eventLogger.beginContext("saveMasterData");
      context.commit("refresh-article-masterdata", {
        language: args.language,
        data: args.articleData.model
      });
      context.commit("update-save-status", { section: "masterdata", running: true });
      logger.info("Saving article master data", { language: args.language, articleId: args.articleData.model.articleId }).alsoToConsole();

      let success;
      try {
        await articleApi.updateMasterData(args.language, args.articleData.model.articleId, args.articleData);
        logger.info("Saving article master data successful", { language: args.language, articleId: args.articleData.model.articleId }).alsoToConsole();
        context.commit("update-save-status", { section: "masterdata", running: false });
        success = true;
      } catch (error) {
        logger
          .error("Saving article master data failed", { language: args.language, articleId: args.articleData.model.articleId, exception: error })
          .alsoToConsole();
        context.commit("update-save-status", { section: "masterdata", running: false, error: error, apiError: error.isAxiosError, success: false });
        success = false;
        throw error;
      } finally {
        logger.complete();
      }
      return success;
    },
    saveGlobalAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveGlobalAttributes");
      const articleId = args.articleId ?? context.state.metadata.articleId;
      context.commit("refresh-article-global-attributes", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      logger.info("Saving global attributes", { language: args.language, articleId: articleId }).alsoToConsole();
      context.commit("update-save-status", { section: "globalAttributes", running: true });
      try {
        await articleApi.updateGlobalAttributes(args.language, articleId, args.model);
        logger.info("Saving global attributes successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        logger.error("Saving global attributes failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        context.commit("update-save-status", { section: "globalAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "globalAttributes", running: false });
    },
    saveVariantAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveVariantAttributes");
      const articleId = args.articleId ?? context.state.metadata.articleId;
      context.commit("refresh-article-variant-attributes", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      logger.info("Saving variant attributes", { language: args.language, articleId: articleId }).alsoToConsole();
      context.commit("update-save-status", { section: "variantAttributes", running: true });
      try {
        await articleApi.updateVariantAttributes(args.language, articleId, args.model);
        logger.info("Saving variant attributes successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "variantAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving variant attributes failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "variantAttributes", running: false });
    },
    saveInvariantAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveInvariantAttributes");
      const articleId = args.articleId ?? context.state.metadata.articleId;
      context.commit("refresh-article-invariant-attributes", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      context.commit("update-save-status", { section: "invariantAttributes", running: true });
      logger.info("Saving invariant attributes", { language: args.language, articleId: articleId }).alsoToConsole();
      try {
        await articleApi.updateInvariantAttributes(args.language, articleId, args.model);
        logger.info("Saving invariant attributes successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "invariantAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving invariant attributes failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      }
      logger.complete();
      context.commit("update-save-status", { section: "invariantAttributes", running: false });
    },
    saveMultiArticleVariantAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveMultiArticleVariantAttributes");
      const articleId = args.articleId ?? context.state.metadata.articleId;

      context.commit("refresh-multi-article-variant-attributes", {
        data: args.data,
        articleId: articleId
      });
      logger.info("Saving multi article variant attributes", { language: args.language, articleId: articleId }).alsoToConsole();
      context.commit("update-save-status", { section: "variantAttributes", running: true });
      try {
        const tasks = args.languages.map((x) => articleApi.updateMultiArticleVariantAttributes(x, articleId, args.data[x].attributes));
        await Promise.all(tasks);
        logger.info("Saving multi article variant attributes successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "variantAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving multi article variant attributes failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "variantAttributes", running: false });
    },
    saveClassifications: async function (context, args) {
      const logger = eventLogger.beginContext("saveClassifications");
      const articleId = args.articleId ?? context.state.metadata.articleId;
      context.commit("refresh-article-classifications", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      context.commit("update-save-status", { section: "classifications", running: true });
      logger.info("Saving classifications", { language: args.language, articleId: articleId }).alsoToConsole();
      try {
        await articleApi.updateClassifications(args.language, articleId, args.model);
        logger.info("Saving classifications successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "classifications", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving classifications failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "classifications", running: false });
    },
    saveAlternateArticleNumbers: async function ({ commit, rootState }, args) {
      const logger = eventLogger.beginContext("saveAlternateArticleNumbers");
      commit("refresh-article-alternative-articlenumbers", {
        data: args.data
      });
      commit("update-save-status", { section: "alternativeArticleNumbers", running: true });
      logger.info("Saving alternate article numbers", { language: args.language, articleId: args.articleId }).alsoToConsole();
      try {
        await articleApi.updateAlternateArticleNumbers(rootState.metadata.contentLanguages.enabled[0].languageCode, args.articleId, args.data);
        logger.info("Saving alternate article numbers successful", { language: args.language, articleId: args.articleId }).alsoToConsole();
      } catch (error) {
        commit("update-save-status", {
          section: "alternativeArticleNumbers",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
        logger.error("Saving alternate article numbers failed", { language: args.language, articleId: args.articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      commit("update-save-status", { section: "alternativeArticleNumbers", running: false });
    },
    saveErpSections: async function (context, args) {
      const logger = eventLogger.beginContext("saveErpSections");
      const articleId = args.articleId ?? context.state.metadata.articleId;

      context.commit("refresh-article-erp-sections", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      context.commit("update-save-status", { section: "erpSections", running: true });
      logger.info("Saving erp sections", { language: args.language, articleId: articleId }).alsoToConsole();
      try {
        await articleApi.updateErpSections(args.language, articleId, args.model);
        logger.info("Saving erp sections successful", { language: args.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "erpSections", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving erp sections failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "erpSections", running: false });
    },
    savePriceAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("savePriceAttributes");
      context.commit("refresh-article-price-attributes", {
        language: args.language,
        data: args.model,
        articleId: args.articleId
      });
      context.commit("update-save-status", { section: "priceAttributes", running: true });
      logger.info("Saving price attributes", { language: args.language, articleId: args.articleId }).alsoToConsole();
      try {
        await articleApi.updatePriceAttributes(args.language, args.articleId, args.model);
        logger.info("Saving price attributes successful", { language: args.language, articleId: args.articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "priceAttributes", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving price attributes failed", { language: args.language, articleId: args.articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "priceAttributes", running: false });
    },
    savePrices: async function (context, args) {
      const logger = eventLogger.beginContext("savePrices");
      const articleId = args.articleId ?? context.state.metadata.articleId;

      context.commit("refresh-article-prices", {
        data: args.model,
        articleId: articleId
      });
      context.commit("update-save-status", { section: "prices", running: true });
      logger.info("Saving prices", { language: args.model.language, articleId: articleId }).alsoToConsole();
      try {
        await articleApi.updatePrices(context.rootState.metadata.contentLanguages.enabled[0].languageCode, articleId, args.model);
        await context.dispatch("loadPrices", { articleId: articleId });

        logger.info("Saving prices successful", { language: args.model.language, articleId: articleId }).alsoToConsole();
      } catch (error) {
        context.commit("update-save-status", { section: "prices", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving prices failed", { language: args.model.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "prices", running: false });
    },
    saveKeywords: async function (context, args) {
      const logger = eventLogger.beginContext("saveKeywords");
      const languages = Object.keys(args.model);
      const articleId = args.articleId ?? context.state.metadata.articleId;

      logger.info("Saving keywords", { languages: args.languages, articleId: articleId }).alsoToConsole();
      context.commit("update-save-status", { section: "keywords", running: true });
      try {
        await articleApi.updateKeywords(args.languages, articleId, args.keywords);
        const tasks = [];
        languages.forEach((lang) => {
          tasks.push(context.dispatch("loadKeywords", { articleId: articleId, language: lang }));
        });
        await Promise.all(tasks);
      } catch (error) {
        context.commit("update-save-status", { section: "keywords", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving keywords failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      }
      logger.complete();
      context.commit("update-save-status", { section: "keywords", running: false });
    },
    saveMediaAssets: async function (context, args) {
      const logger = eventLogger.beginContext("saveMediaAssets");
      const articleId = args.articleId ?? context.state.metadata.articleId;
      context.commit("refresh-article-media-assets", {
        language: args.language,
        data: args.model,
        articleId: articleId
      });
      let changedMediaAssetids = [];
      context.commit("update-save-status", { section: "mediaAssets", running: true });
      try {
        changedMediaAssetids = await articleApi.updateMediaAssets(args.language, articleId, args.model);
      } catch (error) {
        context.commit("update-save-status", { section: "mediaAssets", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving media assets failed", { language: args.language, articleId: articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "mediaAssets", running: false });
      return { language: args.language, changedMediaAssetids };
    },
    saveReferences: async function (context, args) {
      const logger = eventLogger.beginContext("saveReferences");
      context.commit("refresh-article-references", {
        language: args.language,
        data: args.model,
        articleId: args.articleId
      });
      context.commit("update-save-status", { section: "references", running: true });
      try {
        await articleApi.updateReferences(args.language, args.articleId, args.model);
      } catch (error) {
        context.commit("update-save-status", { section: "references", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving references failed", { language: args.language, articleId: args.articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "references", running: false });
    },
    saveTranslatedMasterdata: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedMasterdata");
      context.commit("update-save-status", { section: "translatedMasterdata", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updateMasterdata(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
      } catch (error) {
        context.commit("update-save-status", { section: "translatedMasterdata", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger
          .error("Saving translation tab masterdata failed", { language: args.language, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedMasterdata", running: false });
    },
    saveTranslatedGlobalAttribute: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedGlobalAttribute");
      context.commit("update-save-status", { section: "translatedGlobalAttributes", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updateGlobalAttributes(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
        context.commit("translation-article-global-attributes", {
          data: args.data
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedGlobalAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
        logger
          .error("Saving translation tab global attributes failed", { language: args.language, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedGlobalAttributes", running: false });
    },
    saveTranslatedVariantAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedVariantAttributes");
      context.commit("update-save-status", { section: "translatedVariantAttributes", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updateVariantAttributes(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
        context.commit("translation-article-variant-attributes", {
          data: args.data
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedVariantAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
        logger
          .error("Saving translation tab variant attributes failed", { language: args.language, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedVariantAttributes", running: false });
    },
    saveTranslatedInvariantAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedInvariantAttributes");
      context.commit("update-save-status", { section: "translatedInvariantAttributes", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updateInvariantAttributes(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
        context.commit("translation-article-invariant-attributes", {
          data: args.data
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedInvariantAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
        logger
          .error("Saving translation tab invariant attributes failed", {
            language: args.language,
            variationGroupId: args.variationGroupId,
            exception: error
          })
          .alsoToConsole();
        throw error;
      }
      context.commit("update-save-status", { section: "translatedInvariantAttributes", running: false });
    },
    saveTranslatedPriceAttributes: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedPriceAttributes");
      context.commit("update-save-status", { section: "translatedPriceAttributes", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updatePriceAttributes(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
        context.commit("translation-article-price-attributes", {
          data: args.data
        });
      } catch (error) {
        context.commit("update-save-status", {
          section: "translatedPriceAttributes",
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
        logger
          .error("Saving translation tab price attributes failed", { language: args.language, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedPriceAttributes", running: false });
    },
    saveTranslatedMediaAssets: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedMediaAssets");
      context.commit("update-save-status", { section: "translatedMediaAssets", running: true });
      try {
        const languages = Object.keys(args.data);
        const tasks = languages.map((lang) => translationsApi.updateMediaAssets(lang, args.variationGroupId, args.data[lang]));
        await Promise.all(tasks);
        context.commit("translation-article-media-assets", {
          data: args.data
        });
      } catch (error) {
        context.commit("update-save-status", { section: "translatedMediaAssets", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger
          .error("Saving translation tab media assets failed", { language: args.language, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedMediaAssets", running: false });
    },
    saveTranslatedKeywords: async function (context, args) {
      const logger = eventLogger.beginContext("saveTranslatedKeywords");
      context.commit("update-save-status", { section: "translatedKeywords", running: true });
      try {
        await translationsApi.updateKeywords(args.languages, args.data, args.variationGroupId);

        const source = args.data;
        const translationkeywords = {};
        args.languages.forEach((lang, langIndex) => {
          translationkeywords[lang] = source.map((x, rowIndex) => {
            return args.data[rowIndex][langIndex];
          });
        });

        context.commit("translation-article-keywords", {
          data: translationkeywords
        });
      } catch (error) {
        context.commit("update-save-status", { section: "translatedKeywords", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger
          .error("Saving translation tab keywords failed", { languages: args.languages, variationGroupId: args.variationGroupId, exception: error })
          .alsoToConsole();
        throw error;
      } finally {
        logger.complete();
      }
      context.commit("update-save-status", { section: "translatedKeywords", running: false });
    },
    saveGpsr: async function ({ state, commit }) {
      const logger = eventLogger.beginContext("saveGpsr");
      try {
        if (state.metadata.articleId && state.metadata.articleId !== "") {
          commit("update-save-status", { section: "gpsr", running: true });
          await gpsrApi.assignEconomicOperatorsToArticle({
            articleId: state.metadata.articleId,
            manufacturerId: state.gpsr.manufacturerId,
            responsibleInEuId: state.gpsr.responsibleInEuId
          });
        }
      } catch (error) {
        commit("update-save-status", { section: "gpsr", running: false, error: error, apiError: error.isAxiosError, success: false });
        logger.error("Saving gpsr tab failed", { articleId: state.metadata.articleId, exception: error }).alsoToConsole();
        throw error;
      } finally {
        commit("update-save-status", { section: "gpsr", running: false });
        logger.complete();
      }
    },
    setGpsrAssignments: function (context, args) {
      context.commit("set-gpsr-assignments", args);
    },
    executeDefaultValueListSync: async function ({ state, rootState }, args) {
      const logger = eventLogger.beginContext("executeDefaultValueListSync");
      const progressHandler = args.progressHandler;
      const { t: $t } = i18n.global;
      progressHandler.init($t("valueUnitSync.title"), $t("valueUnitSync.starting")).start().open();
      logger.info("Executing default value list sync for all attributes", { articleId: state.metadata.articleId });
      try {
        if (state.metadata.articleId && state.metadata.articleId !== "") {
          const language = rootState.metadata.contentLanguages.enabled[0];
          progressHandler.update($t("valueUnitSync.syncingAttributes"));
          const minWait = MinWait.begin();
          await articleApi.executeAttributeDefaultValueListSync(language.languageCode, progressHandler.abortSignal);
          await minWait.waitUntilPassed(500);
          progressHandler.update($t("valueUnitSync.syncingGlobalAttributes")).progressTo(33);
          minWait.restart();
          await articleApi.executeGlobalAttributeDefaultValueListSync(language.languageCode, progressHandler.abortSignal);
          await minWait.waitUntilPassed(500);
          progressHandler.update($t("valueUnitSync.syncingErpAttributes")).progressTo(66);
          minWait.restart();
          await articleApi.executeErpAttributeDefaultValueListSync(language.languageCode, progressHandler.abortSignal);
          await minWait.waitUntilPassed(500);
          progressHandler.progressTo(100);
          progressHandler.finish($t("valueUnitSync.syncComplete"));
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          progressHandler.error($t("valueUnitSync.failed"));
          logger.error("Default value list sync failed", { articleId: state.metadata.articleId, exception: error }).alsoToConsole();
          throw error;
        }
      } finally {
        logger.complete();
      }
    }
  }
};
